<template>
  <onboarding-form-layout
    :title="$t('incremental_onboarding.business_profile.title')"
    :loading="submitting"
    :show-previous="!isUpdate"
    :errors="Object.values(getErrors)"
    @proceed="proceed"
    class-name="incremental-bp-container"
    :prefilled="isPrefilled"
    @previous="$emit('previous')"
  >
    <ErrorCodeNotification v-if="errorCodes && errorCodes.length > 0" :error-codes="errorCodes" />
    <div class="s-flex s-flex-col s-gap-4">
      <InputField
        :label="$t('incremental_onboarding.business_profile.fields.website.label')"
        id="website"
        class-name="s-flex-1"
        data-testid="website"
        :show-mandatory="true"
        :value="getField(BusinessProfileField.WEBSITE)"
        :set-value="handleInputChange(BusinessProfileField.WEBSITE)"
        :error="getErrors[BusinessProfileField.WEBSITE]"
        :hint="$t('incremental_onboarding.business_profile.fields.website.instruction')"
      />

      <PhoneInput
        :label="$t('incremental_onboarding.business_profile.fields.phone.label')"
        id="phone"
        data-testid="phone"
        :show-mandatory="true"
        :value="getFieldFormatted(BusinessProfileField.PHONE)"
        :country-code="countryCode"
        :set-value="(countryCode, value) => updatePhone({key: BusinessProfileField.PHONE, countryCode, value})"
        :error="getErrors[BusinessProfileField.PHONE]"
        :hint="$t('incremental_onboarding.business_profile.fields.phone.instruction')"
        class="fs-mask s-flex-1 phone-input"
      />

      <InputTextAreaField
        :label="$t('incremental_onboarding.business_profile.fields.description.label')"
        id="description"
        data-testid="description"
        class-name="s-flex-1"
        :show-mandatory="true"
        :value="getField(BusinessProfileField.DESCRIPTION)"
        :set-value="(value) => updateFieldFromValue({key: BusinessProfileField.DESCRIPTION, value})"
        :error="getErrors[BusinessProfileField.DESCRIPTION]"
        :hint="$t('incremental_onboarding.business_profile.fields.description.instruction')"
      />

      <div class="select-field s-flex-1 s-text-left mcc">
        <span>
          <s-label :label="$t('incremental_onboarding.business_profile.fields.mcc.label')" class="s-font-medium" />
          <span class="s-text-danger-600">*</span>
        </span>
        <s-select
          autofocus
          :searchable="true"
          :clearable="true"
          :options="getMccCodes()"
          @update="validateMerchantCategoryCode"
          :value="getField(BusinessProfileField.MCC)"
          class=" mcc-select"
        >
          <template #option="options">
            <div class="mcc-option s-flex s-gap-2">
              <div class="s-w-9 s-text-neutral-400">
                {{ options.option.value }}
              </div>
              <div>{{ getMccDisplayText(options.option.displayText) }}</div>
            </div>
          </template>
        </s-select>

        <s-inline-error
          v-if="Boolean(getErrors[BusinessProfileField.MCC])"
          :message="getErrors[BusinessProfileField.MCC]"
        />
        <div class="hint s-mt-2 s-leading-snug" v-if="!Boolean(getErrors[BusinessProfileField.MCC])">
          <i18n path="incremental_onboarding.business_profile.fields.mcc.instruction" tag="div">
            <template #whatIsMcc>
              <s-link variant="primary" href="https://www.chargebee.com/docs/2.0/cb_payments_faq.html" target="_blank">
                {{ $t('incremental_onboarding.business_profile.fields.mcc.whatIsMcc') }}
              </s-link>
              <br />
            </template>
            <template #visa>
              <s-link
                variant="primary"
                href="https://usa.visa.com/content/dam/VCOM/download/merchants/visa-merchant-data-standards-manual.pdf"
                target="_blank"
              >
                {{ $t('incremental_onboarding.business_profile.fields.mcc.visa') }}
              </s-link>
            </template>
            <template #mastercard>
              <s-link
                variant="primary"
                href="https://www.mastercard.us/content/dam/public/mastercardcom/na/global-site/documents/quick-reference-booklet-merchant.pdf"
                target="_blank"
              >
                {{ $t('incremental_onboarding.business_profile.fields.mcc.mastercard') }}
              </s-link>
            </template>
          </i18n>
        </div>
      </div>

      <InputField
        :label="$t('incremental_onboarding.business_profile.fields.statementDescriptor.label')"
        id="statementDescriptor"
        class-name="s-flex-1"
        data-testid="statementDescriptor"
        :show-mandatory="true"
        :value="getField(BusinessProfileField.STATEMENT_DESCRIPTOR)"
        :set-value="handleInputChange(BusinessProfileField.STATEMENT_DESCRIPTOR)"
        :error="getErrors[BusinessProfileField.STATEMENT_DESCRIPTOR]"
        :hint="$t('incremental_onboarding.business_profile.fields.statementDescriptor.instruction')"
      />
    </div>

    <s-modal
      :control-variable="showMCCNotSupportedPopup"
      :primary-action="{
        label: $t('global.action.ok'),
        action: cancelUnsupportedMCCPopup
      }"
      :secondary-action="{
        label: null
      }"
      :heading="`${getField(BusinessProfileField.MCC)} ${$t('MCCNotSupported.title')}`"
      size="small"
      action-variant="neutral"
    >
      <template #body>
        <div class="s-text-left">
          {{ $t('MCCNotSupported.subtitle') }}
        </div>
      </template>
    </s-modal>
  </onboarding-form-layout>
</template>

<script>
import merchantOnboardingApi from "@/api/merchantOnboardingApi";
import {OnboardingRequest, OwnershipType} from "@/api/paymentApi";
import InputField from "@/components/common/InputField";
import InputTextAreaField from "@/components/common/InputTextAreaField.vue";
import PhoneInput from "@/components/common/PhoneInput.vue";
import OnboardingFormLayout from "@/components/incremental-onboarding/OnboardingFormLayout.vue";
import {OnboardingFormMixin} from "@/components/incremental-onboarding/OnboardingFormMixin";
import BusinessOwnership from "@/components/onboarding/BusinessOwnership.vue";
import {CountryCodeListSortedByName} from "@/data/country-code-list";
import {default as MccCodes, default as mccCodes} from "@/data/mcc-codes";
import OwnershipTypeList from "@/data/ownership-type-list";
import TypesOfBusinessList from "@/data/type-of-business-list.ts";
import {unsupportedMccCodesList} from "@/data/unsupported-mcc-codes";
import {BusinessProfileField} from "@/store/modules/incremental-onboarding/incrementalOnboardingbusinessProfile";
import {mapActions, mapGetters} from "vuex";
import ErrorCodeNotification from "@/components/incremental-onboarding/ErrorCodeNotification.vue";
import FullStoryHelper from '@/app/utils/fullstory';
import {BUSINESS_PROFILE_PROCEED, UPDATE_BUSINESS_PROFILE_PROCEED} from '@/app/utils/fullstory/constants';

export default {
  name: 'IncrementalOnboardingBusinessProfile',
  components: { ErrorCodeNotification, InputTextAreaField, PhoneInput, InputField, OnboardingFormLayout},
  mixins: [OnboardingFormMixin],
  props: {
    isUpdate: {
      type: Boolean,
      required: false,
      default: false,
    },
    errorCodes: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data: function () {
    return {
      showMCCNotSupportedPopup: false,
    }
  },
  computed: {
    OwnershipType() {
      return OwnershipType
    },
    BusinessOwnership() {
      return BusinessOwnership
    },
    BusinessProfileField() {
      return BusinessProfileField
    },
    getErrorList() {
      return Object.keys(this.getErrors).map(x => {
        return this.getErrors[x]
      })
    },
    countryCode() {
      if (this.getCountryCode === undefined) {
        return CountryCodeListSortedByName.find(x => x.value === this.getCountry)?.dialCode;
      } else {
        return this.getCountryCode
      }
    },
    ...mapGetters('essentials', ['getCountry']),
    ...mapGetters('app', ["getSessionPayfac", 'getMerchantId']),
    ...mapGetters('incrementalOnboardingBusinessProfile',
        ['getField', 'getCountryCode', 'getFieldFormatted', 'isPrefilled',
        'getErrors', 'getIncrementalOnboardingData', 'isDirty']),
  },
  methods: {
    ...mapActions('incrementalOnboardingBusinessProfile', ['updateFieldFromEvent',
      'updateFieldFromValue', 'updatePhone', 'validate', 'setErrors', 'resetDirty']),
    getMccCodes: () => MccCodes,
    getMccDisplayText: (textWithCode) => {
      return textWithCode.split(" - ")[1]
    },
    getMccName(code) {
      return mccCodes.find(x => x.value === code)?.displayText;
    },
    cancelUnsupportedMCCPopup() {
      this.showMCCNotSupportedPopup = false
      this.updateFieldFromValue({key: BusinessProfileField.MCC, value: null});
    },
    validateMerchantCategoryCode(code) {
      if (unsupportedMccCodesList(this.getSessionPayfac).find(e => e === code) !== undefined) {
        this.showMCCNotSupportedPopup = true
      }
      this.updateFieldFromValue({key: BusinessProfileField.MCC, value: code});
    },
    getTypesOfBusiness: () => TypesOfBusinessList,
    getOwnershipTypeList: () => OwnershipTypeList,
    async proceed() {
      const apiCall = async () => {
        if (this.isUpdate) {
          return await merchantOnboardingApi.putBusinessProfile(this.getMerchantId, {
            ...this.getIncrementalOnboardingData,
            requestType: OnboardingRequest.RequestTypeEnum.UPDATEPROFILE,
          });
        }
        return await merchantOnboardingApi.updateBusinessProfile(this.getMerchantId, {
          ...this.getIncrementalOnboardingData,
          requestType: OnboardingRequest.RequestTypeEnum.UPDATEPROFILE,
        });
      };

      // Emit FullStory event
      FullStoryHelper.emitIncrementalOnboardingEvent(BUSINESS_PROFILE_PROCEED,
        UPDATE_BUSINESS_PROFILE_PROCEED, this.isUpdate);

      await this.submitForm(apiCall);
    },
  },
}
</script>

<style lang="scss">
.incremental-bp-container {

  & .mcc {
    & .s-selectmenu-content {
      min-width: auto;
    }
  }

  & .phone-input {
    & .s-selectmenu-content {
      min-width: max-content;
    }
  }
}


</style>
